import Badge from "@/components/core/badge";
import { useModal } from "@/components/core/modals/use-modal";
import { useSalesContractCalendarQuery } from "@/features/dashboard/api/queries/sales-contract-query";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { format } from "date-fns";
import type React from "react";
import { useState } from "react";
import { ContractDetailsModal } from "./contract-details-modal";

export const SalesContractCalendar: React.FC = () => {
	const [currentDate, setCurrentDate] = useState(new Date());
	const modal = useModal();

	const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
	const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

	const { data: events = [], isLoading } = useSalesContractCalendarQuery(format(startOfMonth, "yyyy-MM-dd"), format(endOfMonth, "yyyy-MM-dd"));

	const handleEventClick = (info: { event: { extendedProps: { contract: Record<string, unknown> } } }) => {
		const contract = info.event.extendedProps.contract;
		if (contract) {
			modal.openModal({
				title: "Contract Details",
				description: "View detailed information about this contract",
				component: <ContractDetailsModal contract={contract} />,
				size: "lg",
			});
		}
	};

	const handleDatesSet = (info: { view: { currentStart: Date } }) => {
		setCurrentDate(info.view.currentStart);
	};

	const getStatusBadge = (status: string) => {
		const colorMap: Record<string, string> = {
			active: "green",
			expired: "red",
			cancelled: "yellow",
		};
		return <Badge color={colorMap[status] || "default"}>{status}</Badge>;
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="text-gray-500">Loading calendar...</div>
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg shadow p-6">
			<FullCalendar
				plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
				initialView="dayGridMonth"
				headerToolbar={{
					left: "prev,next today",
					center: "title",
					right: "dayGridMonth,timeGridWeek",
				}}
				events={events.map((event) => ({
					id: event.id,
					title: event.title,
					start: event.start,
					allDay: event.allDay,
					backgroundColor: event.color,
					borderColor: event.color,
					extendedProps: {
						contract_id: event.contract_id,
						type: event.type,
						contract: event.contract,
					},
				}))}
				eventClick={handleEventClick}
				datesSet={handleDatesSet}
				height="auto"
				eventDisplay="block"
				dayMaxEvents={3}
				moreLinkClick="popover"
			/>
		</div>
	);
};
